import type { FC } from 'react';
import type {
    AddressMatches,
    FormControl,
    SupportInputType,
} from 'features/my-zone/support-requests/components/support-requests-modals/types';
import type { ActionCellProps, FileWithUrl, ModalProps } from '@placer-ui/components';
import type { PlaceType } from '@placer-ui/types';
import { VenueFlags } from 'core/entities';
import { EAskFowReviewFormSectionType } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/types';

export type ExtendVenueFlags = VenueFlags | 'unavailable';
export type ParsedDataRowValues = string | number | null | boolean | ExtendVenueFlags[];
export type ParsedDataRow = {
    [key: string]: ParsedDataRowValues;
};

export type ParsedCSVData = {
    data: ParsedDataRow[];
    fields?: string[];
    emptyColumns?: string[];
    sheetsCount?: number;
};

export type ColumnsMatching = Record<string, string>;

export type PropertyManualForm = {
    properties: {
        name: string;
        address: string;
        zip_code?: string;
    }[];
};

export type PropertyFileForm = {
    file: [FileWithUrl] | [];
};

export type PropertyFormValues = {
    additionalInformation: string;
    email?: string;
    id?: string;
    inputType: SupportInputType;
    matching: ColumnsMatching;
    parsedData: ParsedCSVData;
    executiveEmail?: string;
    opportunityUrl?: string;
} & (PropertyManualForm | PropertyFileForm);

export interface FormsSectionManager<Type> {
    formConfig: FormSection<Type>[];
    activeSectionIndex: number;
    setActiveSectionIndex: (index: number) => void;
    nextSection: () => Promise<void>;
    previousSection: () => void;
    formLength: number;
    sectionConfig: FormSection<Type>;
    FormSection: React.FC<RequestPropertyFormProps>;
    isLastStep: boolean;
}

type PrivateFormData = Record<any, any>;

export type RequestPropertyFormProps = {
    sectionManager?: FormsSectionManager<FormSectionType>;
    onNextHandler?: (props?: PrivateFormData) => Promise<void>;
} & PrivateFormData;

type FormSectionButton = {
    preventDefault?: boolean;
    isDisabled?: boolean | ((formControl: FormControl<PropertyFormValues>) => boolean);
    isLoading?: boolean | ((formControl: FormControl<PropertyFormValues>) => boolean);
    isHidden?: boolean | ((formControl: FormControl<PropertyFormValues>) => boolean);
    text?: string;
    testId?: string;
    onClick?: (props: Partial<RequestPropertyFormProps>) => Promise<void | boolean>;
};

export type FormSectionType =
    | 'request-properties'
    | 'match-file-data'
    | 'coverage-check'
    | 'data-validation'
    | 'submit';

export interface FormSection<Type> {
    title?: string;
    testId: string;
    type: Type;
    subTitle?: string;
    Form: FC<RequestPropertyFormProps>;
    formData?: any;
    extraModalProps?: Partial<ModalProps>;
    onStepClose?: (props: Partial<RequestPropertyFormProps>) => void;
    elevioId?: number;
    nextButton?: FormSectionButton;
    prevButton?: FormSectionButton;
}

export type RequestResultsStatus =
    | 'Available'
    | 'MissingInfo'
    | 'SensitiveLocation'
    | 'ReviewRequired'
    | 'ProcessingFailed'
    | 'Edited';

export const enum ResolverErrorCodesEnum {
    NoError = 0,
    NotAvailableVenue = 3000,
    ShadowVenue = 3001,
    UnResolvedVenue = 3002,
    MissingName = 4100,
    MissingLat = 4101,
    MissingLng = 4102,
    MissingCity = 4103,
    MissingState = 4104,
    MissingStreet = 4105,
    MissingFormattedAddress = 4106,
    MissingSomeAddressField = 4107,
    InvalidLat = 4200,
    InvalidLng = 4201,
    InvalidZipCode = 4202,
    InvalidState = 4203,
    InvalidAddress = 5002,
}

export type SupportRequestResultsResponse = SupportRequestChallengeProps & {
    id: string;
    status: RequestResultsStatus;
    field_error_code: ResolverErrorCodesEnum;
    entity_id?: string;
    entity_type?: PlaceType;
    entity_name?: string;
    closing_date?: string;
    entity_address?: string;
    report_type?: 'Property' | 'Nearby' | '';
    flag_reasons?: ExtendVenueFlags[];
    alternative_count?: number;
    alternative_token?: string;
};

type SupportRequestMapper = AddressMatches & {
    name?: string;
    zip_code?: string;
};

export type SupportRequestChallengeProps = {
    challenge_type?: EAskFowReviewFormSectionType;
    challenge_status_override?: RequestResultsStatus;
    challenge_selected_alt?: number;
    challenge_googlemap_link?: string;
    challenge_property_name?: string;
    challenge_property_address?: string;
    challenge_note?: string;
};

export type SupportRequestResultsModalTableRow = SupportRequestMapper &
    SupportRequestResultsResponse & {
        lineId?: string;
        isEdited?: boolean;
        isLoading?: boolean;
        error_message?: string;
        link_to_report?: string;
        closing_date?: string;
        report_type_message?: string | null;
        main_flag_reason?: ExtendVenueFlags;
        actions?: ActionCellProps;
    };

type SupportResultsStatusFilters = {
    [K in RequestResultsStatus]: boolean;
};

export type PartialSupportResultsStatusFilters = Partial<SupportResultsStatusFilters>;

export type LocationsOrderRowStatus =
    'Initial'
    | 'Available'
    | 'MissingInfo'
    | 'ReviewRequired'
    | 'ProcessingFailed'
    | 'SensitiveLocation';

export type LocationsOrderMetadata = {
    order_id: string;
    order_status: 'Open' | 'Resolving' | 'Closed';
    order_owner_username: string;
    created_at: string;
    updated_at: string;
    row_summary: LocationsOrderRowSummary;
}

export type LocationsOrderRowSummary = {
    count: number;
    edited_count: number;
    status_count: Record<LocationsOrderRowStatus, number>;
};

export type CreateLocationsOrderRequest = {
    initial_rows: {
        columns: string[];
        rows: ParsedDataRowValues[][];
    };
};

export type ResolveLocationsOrderResponse = {
    resolving_rows_count: number;
};

export type ResolverJobStatus = { status: string };

export type ResolverJobResponse = {
    job_id: string;
    input_file_path: string;
    status?: string;
};

export type ResolverJobResults = {
    data: {
        columns: string[];
        rows: [Array<string | number>];
    };
};

export type MappedFileData = {
    data: ParsedDataRow[];
    matching: ColumnsMatching;
    id: string;
};
